/**
 * Shared UI Components for Feline-SA
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  X, 
  ChevronDown, 
  AlertCircle, 
  CheckCircle, 
  Info, 
  Loader2,
  Search,
  Menu
} from 'lucide-react';
import { cn, getStatusConfig } from '../../utils/helpers';

// =====================
// BUTTON
// =====================

export const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md',
  loading = false,
  disabled = false,
  className = '',
  ...props 
}) => {
  const variants = {
    primary: 'bg-feline-500 hover:bg-feline-600 text-white shadow-md hover:shadow-lg',
    secondary: 'bg-trust-500 hover:bg-trust-600 text-white shadow-md hover:shadow-lg',
    outline: 'border-2 border-feline-500 text-feline-600 hover:bg-feline-50',
    ghost: 'text-gray-600 hover:text-feline-600 hover:bg-feline-50',
    danger: 'bg-red-500 hover:bg-red-600 text-white shadow-md hover:shadow-lg'
  };

  const sizes = {
    sm: 'py-1.5 px-3 text-sm',
    md: 'py-2.5 px-5',
    lg: 'py-3 px-6 text-lg'
  };

  return (
    <button
      className={cn(
        'font-semibold rounded-lg transition-all duration-200 active:scale-[0.98] inline-flex items-center justify-center gap-2',
        variants[variant],
        sizes[size],
        (disabled || loading) && 'opacity-50 cursor-not-allowed',
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
};

// =====================
// INPUT
// =====================

export const Input = ({ 
  label, 
  error, 
  className = '', 
  ...props 
}) => {
  return (
    <div className="space-y-1.5">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <input
        className={cn(
          'w-full px-4 py-2.5 border rounded-lg outline-none transition-all duration-200',
          'focus:ring-2 focus:ring-feline-300 focus:border-feline-500',
          error ? 'border-red-500 focus:ring-red-300 focus:border-red-500' : 'border-gray-300',
          className
        )}
        {...props}
      />
      {error && (
        <p className="text-sm text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </p>
      )}
    </div>
  );
};

// =====================
// SELECT
// =====================

export const Select = ({ 
  label, 
  error, 
  options = [], 
  placeholder = 'Select...',
  className = '',
  ...props 
}) => {
  return (
    <div className="space-y-1.5">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          className={cn(
            'w-full px-4 py-2.5 border rounded-lg outline-none transition-all duration-200 appearance-none bg-white',
            'focus:ring-2 focus:ring-feline-300 focus:border-feline-500',
            error ? 'border-red-500' : 'border-gray-300',
            className
          )}
          {...props}
        >
          <option value="">{placeholder}</option>
          {options.map((opt) => (
            <option key={opt.value || opt} value={opt.value || opt}>
              {opt.label || opt}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
      </div>
      {error && (
        <p className="text-sm text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </p>
      )}
    </div>
  );
};

// =====================
// TEXTAREA
// =====================

export const Textarea = ({ 
  label, 
  error, 
  className = '', 
  rows = 4,
  ...props 
}) => {
  return (
    <div className="space-y-1.5">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <textarea
        rows={rows}
        className={cn(
          'w-full px-4 py-2.5 border rounded-lg outline-none transition-all duration-200 resize-none',
          'focus:ring-2 focus:ring-feline-300 focus:border-feline-500',
          error ? 'border-red-500' : 'border-gray-300',
          className
        )}
        {...props}
      />
      {error && (
        <p className="text-sm text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </p>
      )}
    </div>
  );
};

// =====================
// CARD
// =====================

export const Card = ({ 
  children, 
  className = '', 
  hover = true,
  ...props 
}) => {
  return (
    <div
      className={cn(
        'bg-white rounded-xl shadow-card p-6 transition-all duration-300',
        hover && 'hover:shadow-card-hover',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

// =====================
// BADGE
// =====================

export const Badge = ({ 
  children, 
  variant = 'default',
  status,
  className = '' 
}) => {
  const variants = {
    default: 'bg-gray-100 text-gray-700',
    primary: 'bg-feline-100 text-feline-700',
    secondary: 'bg-trust-100 text-trust-700',
    success: 'bg-paw-100 text-paw-700',
    warning: 'bg-amber-100 text-amber-700',
    danger: 'bg-red-100 text-red-700'
  };

  // If status is provided, use status config
  if (status) {
    const config = getStatusConfig(status);
    return (
      <span className={cn(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
        config.bgClass,
        config.textClass,
        className
      )}>
        <span className={cn('w-1.5 h-1.5 rounded-full mr-1.5', config.dotClass)} />
        {children || config.label}
      </span>
    );
  }

  return (
    <span className={cn(
      'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
      variants[variant],
      className
    )}>
      {children}
    </span>
  );
};

// =====================
// MODAL
// =====================

export const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  children,
  size = 'md',
  className = ''
}) => {
  if (!isOpen) return null;

  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-full mx-4'
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/50 transition-opacity animate-fade-in"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className={cn(
          'relative bg-white rounded-xl shadow-xl w-full animate-slide-up',
          sizes[size],
          className
        )}>
          {/* Header */}
          {title && (
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <button 
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}
          
          {/* Content */}
          <div className="px-6 py-4">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

// =====================
// ALERT
// =====================

export const Alert = ({ 
  type = 'info', 
  title, 
  children,
  onClose,
  className = '' 
}) => {
  const configs = {
    info: { 
      bg: 'bg-blue-50', 
      border: 'border-blue-200', 
      icon: Info, 
      iconColor: 'text-blue-500',
      titleColor: 'text-blue-800',
      textColor: 'text-blue-700'
    },
    success: { 
      bg: 'bg-paw-50', 
      border: 'border-paw-200', 
      icon: CheckCircle, 
      iconColor: 'text-paw-500',
      titleColor: 'text-paw-800',
      textColor: 'text-paw-700'
    },
    warning: { 
      bg: 'bg-amber-50', 
      border: 'border-amber-200', 
      icon: AlertCircle, 
      iconColor: 'text-amber-500',
      titleColor: 'text-amber-800',
      textColor: 'text-amber-700'
    },
    error: { 
      bg: 'bg-red-50', 
      border: 'border-red-200', 
      icon: AlertCircle, 
      iconColor: 'text-red-500',
      titleColor: 'text-red-800',
      textColor: 'text-red-700'
    }
  };

  const config = configs[type];
  const Icon = config.icon;

  return (
    <div className={cn(
      'rounded-lg border p-4 flex gap-3',
      config.bg,
      config.border,
      className
    )}>
      <Icon className={cn('w-5 h-5 flex-shrink-0 mt-0.5', config.iconColor)} />
      <div className="flex-1">
        {title && (
          <h4 className={cn('font-medium mb-1', config.titleColor)}>{title}</h4>
        )}
        <div className={config.textColor}>{children}</div>
      </div>
      {onClose && (
        <button onClick={onClose} className={cn('flex-shrink-0', config.iconColor)}>
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// =====================
// SEARCH INPUT
// =====================

export const SearchInput = ({ 
  placeholder = 'Search...', 
  value, 
  onChange,
  className = '' 
}) => {
  return (
    <div className={cn('relative', className)}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all duration-200 focus:ring-2 focus:ring-feline-300 focus:border-feline-500"
      />
    </div>
  );
};

// =====================
// LOADING SPINNER
// =====================

export const Spinner = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12'
  };

  return (
    <Loader2 className={cn('animate-spin text-feline-500', sizes[size], className)} />
  );
};

export const LoadingScreen = ({ message = 'Loading...' }) => {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
      <Spinner size="lg" />
      <p className="text-gray-500">{message}</p>
    </div>
  );
};

// =====================
// EMPTY STATE
// =====================

export const EmptyState = ({ 
  icon: Icon, 
  title, 
  description, 
  action 
}) => {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      {Icon && (
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <Icon className="w-8 h-8 text-gray-400" />
        </div>
      )}
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      {description && (
        <p className="text-gray-500 mb-4 max-w-md">{description}</p>
      )}
      {action}
    </div>
  );
};

// =====================
// TAB NAVIGATION
// =====================

export const Tabs = ({ tabs, activeTab, onChange }) => {
  return (
    <div className="border-b border-gray-200">
      <nav className="flex gap-8">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            className={cn(
              'py-3 border-b-2 font-medium text-sm transition-colors',
              activeTab === tab.id
                ? 'border-feline-500 text-feline-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className={cn(
                'ml-2 px-2 py-0.5 rounded-full text-xs',
                activeTab === tab.id
                  ? 'bg-feline-100 text-feline-600'
                  : 'bg-gray-100 text-gray-500'
              )}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </nav>
    </div>
  );
};

// =====================
// STAT CARD
// =====================

export const StatCard = ({ 
  label, 
  value, 
  subValue,
  icon: Icon,
  trend,
  className = '' 
}) => {
  return (
    <Card className={cn('flex items-center gap-4', className)}>
      {Icon && (
        <div className="w-12 h-12 bg-feline-100 rounded-xl flex items-center justify-center">
          <Icon className="w-6 h-6 text-feline-600" />
        </div>
      )}
      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {subValue && (
          <p className={cn(
            'text-sm',
            trend === 'up' ? 'text-paw-600' : trend === 'down' ? 'text-red-600' : 'text-gray-500'
          )}>
            {subValue}
          </p>
        )}
      </div>
    </Card>
  );
};

// =====================
// AVATAR
// =====================

export const Avatar = ({ 
  src, 
  name, 
  size = 'md',
  className = '' 
}) => {
  const sizes = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-14 h-14 text-lg',
    xl: 'w-20 h-20 text-xl'
  };

  const initials = name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className={cn('rounded-full object-cover', sizes[size], className)}
      />
    );
  }

  return (
    <div className={cn(
      'rounded-full bg-feline-100 text-feline-600 font-semibold flex items-center justify-center',
      sizes[size],
      className
    )}>
      {initials}
    </div>
  );
};
